import { useEffect, useMemo, useState } from 'react';
import { Row, Col, Modal, Form, Tabs } from 'antd';
import { useIntl } from 'react-intl';
import { DocumentData } from '../types';
import { useDocumentProcessing } from './DocumentProcessingContext';
import PDFViewer from './PDFViewer';
import PDFSplitter from './PDFSplitter';
import ValidatorAiReview from './ValidatorAiReview';
import DynamicForm from './DynamicForm';
import { addRequiredSections, clearEmptyMultivalueSections, resolveValidatorReview, sanitizeNumberFields } from '../utils';
import { moveEmailInfoSectionToBottom } from './contants';
import {
    AssessmentReportRepliesList,
    fetchAssessmentReportReplies,
    type AssessmentReportReply,
} from './AssessmentReportReplies';

interface DocumentProcessingDetailPageProps {
  document: DocumentData;
  title: string;
  isOpen: boolean;
  closeModal: () => void;
}

const DocumentProcessingDetailPage: React.FC<DocumentProcessingDetailPageProps> = ({ document, title, isOpen }) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('view');
  const intl = useIntl();
  const { pdfDetails, closeModal, selectedDocument, tabKey, commonHeaders } = useDocumentProcessing();

  const landingPageDocId = selectedDocument?.landingPageDocId;
  const [replies, setReplies] = useState<AssessmentReportReply[]>([]);
  const [repliesLoading, setRepliesLoading] = useState(false);
  const [repliesError, setRepliesError] = useState(false);

  useEffect(() => {
    if (!landingPageDocId) {
      setReplies([]);
      setRepliesError(false);
      return;
    }

    setRepliesLoading(true);
    setRepliesError(false);
    fetchAssessmentReportReplies(landingPageDocId, commonHeaders)
      .then(setReplies)
      .catch(() => setRepliesError(true))
      .finally(() => setRepliesLoading(false));
  }, [landingPageDocId, commonHeaders]);

  // This component stays mounted across opens (only Modal visibility toggles),
  // so the tab needs to be reset per-document rather than just at first mount —
  // jump straight to the reply when one already exists, since that's the more
  // relevant "result" for an assessment report than the raw original PDF.
  useEffect(() => {
    if (!isOpen) return;
    const hasReply = selectedDocument?.assessmentReportStatus === 'reply_received'
      || selectedDocument?.assessmentReportStatus === 'completed';
    setActiveTab(hasReply ? 'replies' : 'view');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedDocument?.jsonId]);

  const schemaData = useMemo(() => {
    const result = selectedDocument?.jsonContent?.Metadata?.results?.[0];
    if (!result || !Array.isArray(result.content)) {
      return null;
    }

    const requiredSchema = addRequiredSections(result, [
      { sectionSchemaId: 'price_adjustment_items', childSchemaIds: ['item_price_factory_no'] },
      { sectionSchemaId: 'line_item_section', childSchemaIds: ['item_factory_id'] },
      { sectionSchemaId: 'general_info_section', childSchemaIds: ['item_factory_id'] },
      { sectionSchemaId: 'line_items_section', childSchemaIds: ['item_factory_id'] },
    ]);

    const sanitized = sanitizeNumberFields(requiredSchema);
    const cleaned = clearEmptyMultivalueSections(sanitized);
    return moveEmailInfoSectionToBottom(cleaned);
  }, [selectedDocument]);

  const validatorReview = useMemo(
    () => resolveValidatorReview(pdfDetails, selectedDocument),
    [pdfDetails, selectedDocument],
  );

  const pdfFilename = pdfDetails?.filename || pdfDetails?.fileName || selectedDocument?.filename || '';

  if (!pdfDetails?.url || !schemaData) return null;

  return (
      <Modal
          title={title}
          visible={isOpen}
          onCancel={closeModal}
          footer={null}
          width="100%"
          style={{
        top: 0,
        maxWidth: '100vw',
        padding: 0,
      }}
          bodyStyle={{
        padding: 0,
        maxHeight: '100%',
      }}
    >
          <Row>
              {activeTab !== 'replies' && (
                  <Col span={10}>
                      <DynamicForm schemaData={schemaData} form={form} />
                  </Col>
              )}
              <Col span={activeTab === 'replies' ? 24 : 14}>
                  <Tabs
                      activeKey={activeTab}
                      onChange={setActiveTab}
                      destroyInactiveTabPane
                      style={{ padding: '0 16px' }}
                  >
                      <Tabs.TabPane
                          tab={intl.formatMessage({ id: 'document.processing.action.view' })}
                          key="view"
                      >
                          <PDFViewer pdfUrl={pdfDetails.url} filename={pdfFilename} />
                      </Tabs.TabPane>
                      {activeTab !== 'replies' && tabKey !== '2' && tabKey !== '7' && tabKey !== '8' && (
                          <Tabs.TabPane
                              tab={intl.formatMessage({ id: 'document.processing.action.split' })}
                              key="split"
                          >
                              <PDFSplitter
                                  pdfUrl={pdfDetails.url || null}
                                  filename={selectedDocument?.filename || ''}
                              />
                          </Tabs.TabPane>
                      )}
                      {landingPageDocId && (
                          <Tabs.TabPane
                              tab={intl.formatMessage({ id: 'guest.documents.viewReplies', defaultMessage: 'Replies' })}
                              key="replies"
                          >
                              <div style={{ display: 'flex', height: '90vh' }}>
                                  <div style={{ width: '65%', flexShrink: 0, borderRight: '1px solid #f0f0f0', padding: 16 }}>
                                      <PDFViewer pdfUrl={pdfDetails.url} filename={pdfFilename} hideControls />
                                  </div>
                                  <div style={{ width: '35%', flexShrink: 0, minWidth: 0, padding: 16, overflowY: 'auto' }}>
                                      <AssessmentReportRepliesList
                                          loading={repliesLoading}
                                          error={repliesError}
                                          replies={replies}
                                          loadErrorMessage={intl.formatMessage({ id: 'guest.documents.replies.loadError', defaultMessage: "Couldn't load replies" })}
                                          emptyMessage={intl.formatMessage({ id: 'document.processing.landing.replies.staffEmpty', defaultMessage: 'The AI agent is extracting data from the document. Please wait and refresh in a few minutes.' })}
                                      />
                                  </div>
                              </div>
                          </Tabs.TabPane>
                      )}
                      {activeTab !== 'replies' && (
                          <Tabs.TabPane
                              tab={intl.formatMessage({ id: 'document.processing.action.validatorReview' })}
                              key="validatorReview"
                          >
                              <ValidatorAiReview review={validatorReview} />
                          </Tabs.TabPane>
                      )}
                  </Tabs>
              </Col>
          </Row>
      </Modal>
  );
};

export { DocumentProcessingDetailPage };
